// ── Lo que la llamada dice y enseña, sin modelo ──────────────────────────────
//
// Funciones puras, sin Vue ni navegador, para que se puedan probar con
// node --test: la tarjeta leída en voz alta, la línea «He entendido», una frase
// del servidor ({ text, key, params }) en el idioma de quien escucha, y qué
// pista dar cuando el micrófono no deja.
//
// `t`/`te` son los de vue-i18n; `locale` el idioma de la pantalla.

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Una frase del servidor en el idioma de quien mira; lo que no trae clave, tal cual. */
export function phraseOf(said, { t, te }) {
  if (!said) return ''
  if (typeof said === 'string') return said
  return said.key && te(said.key) ? t(said.key, said.params ?? {}) : (said.text ?? '')
}

/** «jueves, 8 de octubre» (y la hora, si la hay). */
export function dayOf(value, { locale = 'es', detail = '' } = {}) {
  if (!ISO_DAY.test(String(value ?? ''))) return String(value ?? '')
  const day = new Date(`${value}T12:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })
  return detail ? `${day}, ${detail}` : day
}

/**
 * Los campos de una tarjeta separados por lo que son, sin saber de qué módulo
 * viene: el día es el campo que se edita como fecha, la gente el buscador
 * múltiple, y los sitios los campos que nombran algo y no se editan (cada
 * sitio de una ronda va como una fila con sus tareas).
 */
export function cardParts(action) {
  const fields = action?.presentation?.fields ?? []
  const filled = (field) => field?.value && !field.value.key && String(field.value.text ?? '').trim()
  const day = fields.find(field => field.edit?.type === 'date' && filled(field))
  const people = fields.find(field => field.edit?.type === 'reference' && field.edit?.multiple && filled(field))
  const places = fields.filter(field => !field.edit && filled(field) && !field.label?.key)
  return { fields, day, people, places, filled }
}

/**
 * «He entendido: Mas Pla · jueves, 8 de octubre · Jordi, Marta». Lo que la
 * persona tiene que poder comprobar de un vistazo antes de decir «sí»: dónde,
 * cuándo y quién. Vacío cuando no hay nada de eso.
 */
export function understoodOf(action, { locale = 'es' } = {}) {
  const { day, people, places } = cardParts(action)
  const bits = [
    places.map(field => field.label?.text ?? '').filter(Boolean).join(', '),
    day ? dayOf(day.value.text, { locale, detail: day.detail }) : '',
    people ? people.value.text : '',
  ].filter(Boolean)
  return bits.join(' · ')
}

/**
 * La tarjeta leída en voz alta: lo que se ve, en pocas palabras, y la
 * pregunta. Lo vacío («Nadie todavía») no se lee: está en pantalla.
 */
export function readBackOf(action, { t, locale = 'es' }) {
  if (action?.handoff) return t('assistant.call.handoff')
  // La pregunta del módulo, si la hay, es la más precisa: «"cortar los setos"
  // no está en vuestras tareas, ¿es Poda…?» dice más que un «¿cuál?». Y lo
  // que aún falta se pregunta: nada se presenta hasta tenerlo todo.
  if (action?.question) return phraseOf(action.question, { t, te: (key) => t(key) !== key })
  // Una duda con candidatos se pregunta antes que nada: «¿te refieres a…?» y
  // un «sí» la resuelve; «¿cuál…?» y «el 23» la resuelve. Sin preguntar por
  // crear la orden hasta que no quede ninguna.
  const doubt = (action?.presentation?.fields ?? []).find(field => field.doubt && field.options?.length)
  if (doubt) {
    const said = doubt.search || ''
    const names = doubt.options.map(option => option.name)
    if (names.length === 1) return t('assistant.call.didYouMean', { said, name: names[0] })
    const last = names.pop()
    return t('assistant.call.whichOne', { said, options: names.join(', '), last })
  }
  // La orden entera, en las piezas que escribió el módulo: el día se dice en
  // el idioma de quien escucha («martes, 6 de octubre»).
  const spoken = action?.presentation?.spoken ?? []
  if (spoken.length) {
    const said = spoken.map((part) => {
      const params = { ...(part.params ?? {}) }
      if (ISO_DAY.test(String(params.day ?? ''))) params.day = dayOf(params.day, { locale })
      return part.key && t(part.key, params) !== part.key ? t(part.key, params) : (part.text ?? '')
    }).filter(Boolean)
    if (said.length) return `${said.join('. ')}. ${t('assistant.confirmAloud')}`
  }
  const { fields, filled } = cardParts(action)
  const parts = fields.filter(filled).slice(0, 4).map((field) => {
    const value = String(field.value.text)
    if (field.edit?.type === 'date') return dayOf(value, { locale, detail: field.detail })
    return field.label?.key ? value : `${field.label?.text ?? ''}: ${value}`
  })
  if (parts.length) return `${parts.join('. ')}. ${t('assistant.confirmAloud')}`
  // Sin campos, la frase del módulo si se puede decir en el idioma de quien
  // escucha (con su clave): un «sí» confirma lo que se oyó, no lo que se ve.
  const phrase = action?.phrase
  if (phrase?.key && t(phrase.key, phrase.params ?? {}) !== phrase.key) {
    return `${t(phrase.key, phrase.params ?? {})}. ${t('assistant.confirmAloud')}`
  }
  return t('assistant.proposalOnScreen')
}

/** Dónde se da permiso al micrófono, según el aparato: la pista que sirve. */
export function micPlatform(userAgent = '') {
  const agent = String(userAgent)
  if (/iPhone|iPad|iPod/i.test(agent)) return 'ios'
  if (/Android/i.test(agent)) return 'android'
  return 'desktop'
}

/**
 * Las frases fijas de la llamada, para pedir su voz al empezar y que suenen
 * al instante cuando tocan (en paralelo con la prueba del micrófono).
 */
export function fixedPhrases(t) {
  return [
    'assistant.call.greeting', 'assistant.call.stillThere', 'assistant.call.cancelled',
    'assistant.call.failed', 'assistant.call.bye', 'assistant.call.heardNothing',
    'assistant.proposalOnScreen', 'assistant.outcome.done',
    ...HOLD_KEYS,
  ].map(key => t(key))
}

// ── «Un momento, lo miro» ────────────────────────────────────────────────────
//
// Cuando la respuesta va a tardar, la llamada lo dice en vez de quedarse muda:
// en cuanto el agente se pone a consultar algo (`tool`), o si pasado
// HOLD_AFTER_MS desde que se oyó la frase aún no hay respuesta. Lo que contestan
// las reglas llega antes y no la oye nadie.
//
// Tres frases que se turnan, una vez por turno como mucho, y la respuesta
// espera a que la frase acabe: ni se pisan ni suena «un momento» dos veces
// seguidas con las mismas palabras. Son frases fijas, así que su voz se pide al
// descolgar con las demás (fixedPhrases) y la pasarela la guarda por texto y
// voz: ninguna llamada a la síntesis por turno.

/** Cuánto silencio, desde que se oyó la frase, antes de decir «un momento». */
export const HOLD_AFTER_MS = 1500

export const HOLD_KEYS = ['assistant.call.holdOn', 'assistant.call.holdOnAgain', 'assistant.call.holdOnStill']

/**
 * La espera de un turno. `say(text)` devuelve una promesa que se cumple al
 * acabar de decirlo. Por turno: `begin()`; `heard()` cuando llega lo oído
 * (arma el reloj); `busy()` cuando el agente consulta algo (lo dice ya);
 * `settle()` cuando llega la respuesta — desarma el reloj y espera a que la
 * frase de espera, si sonaba, acabe.
 *
 * @param {object} options
 * @param {(text: string) => Promise<void>|void} options.say
 * @param {string[]} options.phrases  ya traducidas (HOLD_KEYS con `t`)
 * @param {number} [options.delayMs]
 * @param {object} [options.timers]   `{ set, clear }`, para los tests
 */
export function createHold({ say, phrases, delayMs = HOLD_AFTER_MS, timers = { set: setTimeout, clear: clearTimeout } }) {
  let turn = null
  let next = 0

  function fire(current) {
    if (!current || current !== turn || current.over || current.said || !phrases.length) return
    if (current.timer) { timers.clear(current.timer); current.timer = null }
    current.said = phrases[next % phrases.length]
    next += 1
    current.playing = Promise.resolve().then(() => say(current.said)).catch(() => {})
  }

  return {
    begin() {
      if (turn?.timer) timers.clear(turn.timer)
      if (turn) turn.over = true
      turn = { said: '', over: false, timer: null, playing: Promise.resolve() }
    },
    heard() {
      const current = turn
      if (!current || current.over || current.said || current.timer) return
      current.timer = timers.set(() => fire(current), delayMs)
    },
    busy() { fire(turn) },
    async settle() {
      const current = turn
      if (!current) return ''
      current.over = true
      if (current.timer) { timers.clear(current.timer); current.timer = null }
      await current.playing
      return current.said
    },
  }
}

/** Las fases de la llamada, en el orden en que se entienden. */
export const PHASES = ['connecting', 'listening', 'processing', 'thinking', 'speaking', 'paused', 'offline', 'error', 'ended']
